/**
 * Oil Service
 * 
 * Service for fetching oil data with comprehensive error handling.
 * 
 * Note: Caching is currently disabled to ensure fresh data on every request.
 */

import { apiClient, ApiError, ApiErrorType } from '../api';
import { OilResponse } from './types';


// Retry configuration for recoverable errors
const RETRY_CONFIG = {
    maxAttempts: 2, // Reduced from 3 to 2 to minimize excessive retries
    baseDelay: 1000, // 1 second
    maxDelay: 3000,  // Reduced from 5 to 3 seconds
    backoffMultiplier: 2
};

/**
 * Maps API errors to oil-specific errors with appropriate fallback handling
 * @param error - The original error from API call
 * @param operation - The oil operation that failed
 * @returns Error with appropriate message
 */
const mapApiError = (error: unknown, operation: string): Error => {
    if (error instanceof ApiError) {
        switch (error.type) {
            case ApiErrorType.NOT_FOUND:
                return new Error("Oil data not found. Try selecting different date range or position.");

            case ApiErrorType.NETWORK:
            case ApiErrorType.TIMEOUT:
                return new Error(
                    'Network connectivity issue. Please check your connection and try again.',
                );

            case ApiErrorType.SERVER:
                return new Error(
                    'The oil service is temporarily unavailable. Please try again later.',
                );

            case ApiErrorType.UNAUTHORIZED:
            case ApiErrorType.FORBIDDEN:
                return new Error(
                    'Access denied. Please check your permissions.',
                );

            case ApiErrorType.VALIDATION:
                return new Error(
                    'Invalid request parameters. Please verify the equipment position.',
                );
        }
    }

    // Handle unknown errors
    return new Error(
        'An unknown error occurred while processing oil data'
    );
};

/**
 * Determines if an error is recoverable and should be retried
 * @param error - The error to check
 * @returns True if the error is recoverable, false otherwise
 */
const isRecoverableError = (error: unknown): boolean => {
    if (error instanceof ApiError) {
        // Retry network, timeout, and server errors
        return error.type === ApiErrorType.NETWORK ||
            error.type === ApiErrorType.TIMEOUT ||
            error.type === ApiErrorType.SERVER;
    }

    return false;
};

/**
 * Implements exponential backoff delay for retry attempts
 * @param attempt - Current attempt number (0-based)
 * @returns Delay in milliseconds
 */
const calculateRetryDelay = (attempt: number): number => {
    const delay = RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
    return Math.min(delay, RETRY_CONFIG.maxDelay);
};

/**
 * Sleeps for the specified number of milliseconds
 * @param ms - Milliseconds to sleep
 */
const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Executes a function with retry logic for recoverable errors
 * @param operation - The async operation to execute
 * @param operationName - Name of the operation for error messages
 * @returns Promise resolving to the operation result
 */
const executeWithRetry = async <T>(
    operation: () => Promise<T>,
    operationName: string
): Promise<T> => {
    let lastError: unknown;

    for (let attempt = 0; attempt < RETRY_CONFIG.maxAttempts; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;

            // Don't retry if error is not recoverable or if this is the last attempt
            if (!isRecoverableError(error) || attempt === RETRY_CONFIG.maxAttempts - 1) {
                throw error;
            }

            // Calculate delay and wait before retry
            const delay = calculateRetryDelay(attempt);
            console.warn(
                `${operationName} failed (attempt ${attempt + 1}/${RETRY_CONFIG.maxAttempts}), retrying in ${delay}ms:`,
                error
            );

            await sleep(delay);
        }
    }

    // This should never be reached, but throw the last error as fallback
    throw lastError;
};

/**
 * Ultrasound Service with methods for fetching ultrasound measurement data
 */
export const oilService = {

    async getOilSamplesOld(
        site: string,
        asset: string,
        position: string,
        startDate: string,
        endDate: string,
    ): Promise<OilResponse> {

        const operation = "oil";
        try {
            const encodedSite = encodeURIComponent(site);
            const encodedAsset = encodeURIComponent(asset);
            const encodedPosition = encodeURIComponent(position);
            // Execute API request with retry logic for recoverable errors
            const data = await executeWithRetry(async () => {
                const response = await apiClient.get<OilResponse>(
                    `/${operation}/${encodedSite}/${encodedAsset}/${encodedPosition}`,
                    {
                        params: {
                            start_date: startDate,
                            end_date: endDate,
                        }
                    }
                );
                console.log(`[oil-service.ts] getOilSamples response: ${response}`);
                // Validate response data
                if (!response || !response.samples) {
                    throw new Error('Invalid position');
                }

                return response;
            }, `Oil data retrieval for ${site}/${asset}/${position}`);


            return data;
        } catch (error) {
            throw mapApiError(error, operation);
        }
    },

    async getOilSamples(
        site: string,
        asset: string,
        position: string,
        startDate: string,
        endDate: string,
    ): Promise<OilResponse> {

        const operation = "oil-samples";
        try {
            const encodedSite = encodeURIComponent(site);
            const encodedAsset = encodeURIComponent(asset);
            const encodedPosition = encodeURIComponent(position);
            // Execute API request with retry logic for recoverable errors
            const data = await executeWithRetry(async () => {
                const response = await apiClient.get<OilResponse>(
                    `/${operation}`,
                    {
                        params: {
                            site: encodedSite,
                            asset: encodedAsset,
                            position: encodedPosition,
                            start_date: startDate,
                            end_date: endDate,
                        }
                    }
                );
                console.log(`[oil-service.ts] getOilSamples response: ${response}`);
                // Validate response data
                if (!response || !response.samples) {
                    throw new Error('Invalid position');
                }

                return response;
            }, `Oil data retrieval for ${site}/${asset}/${position}`);


            return data;
        } catch (error) {
            throw mapApiError(error, operation);
        }
    },
};
